import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Scale, DollarSign, Gift, Tag as TagIcon, ShieldAlert } from "lucide-react";
import ReglasProntoPagoPage from "@/pages/ReglasProntoPagoPage";
import ComisionesPage from "@/pages/ComisionesPage";
import ListasPreciosPage from "@/pages/ListasPreciosPage";
import ExcepcionesPage from "@/pages/ExcepcionesPage";
import { useAuth, useHasPerm } from "@/context/AuthContext";

/**
 * Hub `/reglas` — tabs para descuentos, excepciones de precio, comisiones y
 * políticas de precio. Cada tab se muestra sólo si el usuario tiene el permiso
 * mínimo requerido, de modo que un vendedor con `excepciones.aprobar` puede
 * llegar aquí desde el sidebar y ver únicamente la tab "Excepciones de precio".
 */
export default function ReglasHubPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const canAprobarExc = useHasPerm("excepciones.aprobar");
  const canVerComRaw = useHasPerm("comisiones.ver");
  const canAdmComRaw = useHasPerm("comisiones.administrar");
  const canVerComisiones = canVerComRaw || canAdmComRaw;

  const TABS = [
    { key: "descuentos", label: "Descuentos", icon: Gift, Component: ReglasProntoPagoPage, testid: "tab-descuentos", visible: isDirector },
    { key: "excepciones", label: "Excepciones de precio", icon: ShieldAlert, Component: ExcepcionesPage, testid: "tab-excepciones", visible: isDirector || canAprobarExc },
    { key: "comisiones", label: "Comisiones", icon: DollarSign, Component: ComisionesPage, testid: "tab-comisiones", visible: isDirector || canVerComisiones },
    { key: "precios", label: "Políticas de precio", icon: TagIcon, Component: ListasPreciosPage, testid: "tab-precios", visible: isDirector },
  ];
  const visibles = TABS.filter((t) => t.visible);

  const initial = (() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    if (t && visibles.find((v) => v.key === t)) return t;
    return visibles[0]?.key || "descuentos";
  })();
  const [tab, setTab] = useState(initial);

  // Nov/2026: si la URL cambia (KPI → /finanzas?tab=x estando ya en el hub), seguir el tab
  const _loc = useLocation();
  useEffect(() => {
    const t = new URLSearchParams(_loc.search).get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_loc.search]);
  const setTabAndUrl = (t) => {
    setTab(t);
    const url = new URL(window.location);
    url.searchParams.set("tab", t);
    window.history.replaceState({}, "", url);
  };
  const Active = visibles.find((t) => t.key === tab) || visibles[0];

  if (!Active) {
    return (
      <div className="p-12 text-center text-muted-foreground">
        No tienes acceso a ninguna sección de Reglas.
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-primary mb-2">Reglas del negocio</div>
        <h1 className="text-4xl font-display font-bold text-foreground flex items-center gap-3">
          <Scale className="w-8 h-8 text-primary" /> Reglas
        </h1>
      </header>

      <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
        {visibles.map((t) => (
          <button
            key={t.key}
            onClick={() => setTabAndUrl(t.key)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 whitespace-nowrap ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={t.testid}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="hub-embedded hub-embedded-sub"><Active.Component embedded={true} /></div>
    </div>
  );
}
